import axios from 'axios';

const API_BASE_URL = 'https://api.mediscribe.io/api';
const API_CLARA_LLM_BASE_URL = 'https://agents.soluntech.com';
const DEEPGRAM_API_KEY = '1910351be598c5f40909cf907e6a0b3e678538b8';

export { API_BASE_URL, API_CLARA_LLM_BASE_URL, DEEPGRAM_API_KEY };

export const ENDPOINTS = {
  ANALYSES: `${API_BASE_URL}/analyses`,
  GENERATE_TITLE: `${API_CLARA_LLM_BASE_URL}/generate-title`,
  PROCESS_ANALYSIS: `${API_CLARA_LLM_BASE_URL}/process-analysis`,
  CHAT: `${API_CLARA_LLM_BASE_URL}/chat`,
};

// Google OAuth URL
export const GOOGLE_OAUTH_URL = `${API_BASE_URL}/connect/google`;

// Global tracking function reference
let globalTrackRequest: ((promise: Promise<any>, type: string) => Promise<any>) | null = null;

// Function to set the global tracking function
export const setGlobalRequestTracker = (trackRequest: (promise: Promise<any>, type: string) => Promise<any>) => {
  globalTrackRequest = trackRequest;
  console.log('🔄 Global request tracker set');
};

// Create a function to clear all user data
export const clearAllUserData = () => {
  console.log('🧹 Clearing all user data from localStorage');
  const keysToRemove = [
    'token',
    'mediscribe_user',
    'selectedMicrophone',
    'theme',
    'language'
  ];
  
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Also clear axios authorization header
  delete axios.defaults.headers.common['Authorization'];
  
  console.log('✅ All user data cleared from localStorage');
};

// Add axios response interceptor to handle token expiration and track errors
axios.interceptors.response.use(
  (response) => {
    // If response is successful, return it
    return response;
  },
  (error) => {
    console.log('🔍 Axios interceptor caught error:', error);
    
    // Track the error if we have a global tracker
    if (globalTrackRequest) {
      console.log('🔄 Tracking error through global tracker');
      // Create a rejected promise to track
      const errorPromise = Promise.reject(error);
      globalTrackRequest(errorPromise, 'axios').catch(() => {
        // Error already handled by tracker
      });
    }
    
    // Check if error is due to authentication issues
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.log('🚨 Authentication error detected - clearing user data');
      clearAllUserData();
      
      // Redirect to login only if not already there
      if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
        console.log('🔄 Redirecting to login page');
        window.location.href = '/';
      }
    }
    
    // Check for network errors or timeouts
    if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED') {
      console.log('🌐 Network error detected:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Helper function to validate JWT token format
export const isValidTokenFormat = (token: string): boolean => {
  if (!token) return false;
  
  // JWT tokens should have 3 parts separated by dots
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  
  try {
    // Try to decode the payload to check if it's valid base64
    const payload = JSON.parse(atob(parts[1]));
    
    // Check if token has expiration and if it's expired
    if (payload.exp) {
      const currentTime = Math.floor(Date.now() / 1000);
      if (payload.exp < currentTime) {
        console.log('🕐 Token has expired');
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.log('🚨 Invalid token format:', error);
    return false;
  }
};

export const updateAnalysisTranscription = async (analysisId: number, transcriptions: any[]) => {
  try {
    const request = axios.put(`${API_BASE_URL}/analyses/${analysisId}`, {
      data: {
        transcription: transcriptions
      }
    }, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    
    // Track the request if we have a global tracker
    const trackedRequest = globalTrackRequest 
      ? globalTrackRequest(request, 'updateTranscription')
      : request;
    
    const response = await trackedRequest;
    console.log('✅ Analysis updated with transcription:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error updating analysis with transcription:', error);
    throw error;
  }
};

export const updateAnalysisFile = async (analysisId: number, fileData: { pdf: number }, i18n?: any) => {
  try {
    const request = axios.put(`${API_BASE_URL}/analyses/${analysisId}`, {
      data: fileData
    }, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    
    // Track the request if we have a global tracker
    const trackedRequest = globalTrackRequest 
      ? globalTrackRequest(request, 'updateFile')
      : request;
    
    const response = await trackedRequest;
    console.log('✅ Analysis updated with PDF file:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error updating analysis with PDF:', error);
    throw error;
  }
};

export const updateAnalysisReport = async (analysisId: number, sectionKey: string, content: string) => {
  try {
    const request = axios.put(`${API_BASE_URL}/analyses/${analysisId}`, {
      data: {
        [sectionKey]: content
      }
    }, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    
    // Track the request if we have a global tracker
    const trackedRequest = globalTrackRequest 
      ? globalTrackRequest(request, 'updateReport')
      : request;
    
    const response = await trackedRequest;
    console.log('✅ Analysis section updated:', sectionKey, response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error updating analysis section:', error);
    throw error;
  }
};

export const saveDiffToAnalysis = async (analysisId: number, sectionKey: string, diff: any) => {
  try {
    console.log('🔄 API: Iniciando guardado de diff');
    console.log('🔄 API: analysisId:', analysisId);
    console.log('🔄 API: sectionKey:', sectionKey);
    console.log('🔄 API: diff recibido:', diff);
    console.log('🔄 API: tipo de diff:', typeof diff);
    console.log('🔄 API: JSON.stringify del diff:', JSON.stringify(diff));
    
    // Get current analysis to merge with existing diffs
    console.log('🔄 API: Obteniendo análisis actual...');
    const currentAnalysisRequest = axios.get(`${API_BASE_URL}/analyses/${analysisId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    // Track the request if we have a global tracker
    const trackedCurrentAnalysisRequest = globalTrackRequest 
      ? globalTrackRequest(currentAnalysisRequest, 'getCurrentAnalysis')
      : currentAnalysisRequest;

    const currentAnalysis = await trackedCurrentAnalysisRequest;

    console.log('🔄 API: Análisis actual obtenido:', currentAnalysis.data);
    const existingDiffs = currentAnalysis.data.data.attributes.diffs || {};
    console.log('🔄 API: Diffs existentes:', existingDiffs);
    
    const sectionDiffs = existingDiffs[sectionKey] || [];
    console.log('🔄 API: Diffs de la sección actual:', sectionDiffs);
    
    // Add new diff to section
    const updatedSectionDiffs = [...sectionDiffs, diff];
    console.log('🔄 API: Diffs actualizados de la sección:', updatedSectionDiffs);
    
    // Update the diff structure
    const updatedDiffs = {
      ...existingDiffs,
      [sectionKey]: updatedSectionDiffs
    };
    console.log('🔄 API: Estructura completa de diffs actualizada:', updatedDiffs);

    console.log('🔄 API: Enviando actualización al servidor...');
    const updateRequest = axios.put(`${API_BASE_URL}/analyses/${analysisId}`, {
      data: {
        diffs: updatedDiffs
      }
    }, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    
    // Track the request if we have a global tracker
    const trackedUpdateRequest = globalTrackRequest 
      ? globalTrackRequest(updateRequest, 'saveDiff')
      : updateRequest;
    
    const response = await trackedUpdateRequest;
    console.log('✅ API: Diff saved to analysis:', sectionKey, response.data);
    return response.data;
  } catch (error) {
    console.error('❌ API: Error saving diff to analysis:', error);
    if (error.response) {
      console.error('❌ API: Error response:', error.response.data);
      console.error('❌ API: Error status:', error.response.status);
    }
    throw error;
  }
};

export const updateAnalysisIaReport = async (analysisId: number, sectionKey: string, content: string) => {
  try {
    // First, get the current analysis to preserve existing iaReport content
    const currentAnalysisRequest = axios.get(`${API_BASE_URL}/analyses/${analysisId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    // Track the request if we have a global tracker
    const trackedCurrentAnalysisRequest = globalTrackRequest 
      ? globalTrackRequest(currentAnalysisRequest, 'getCurrentAnalysisForIaReport')
      : currentAnalysisRequest;

    const currentAnalysis = await trackedCurrentAnalysisRequest;
    const existingIaReport = currentAnalysis.data.data.attributes.iaReport || {};
    
    // Update only the specific section in iaReport
    const updatedIaReport = {
      ...existingIaReport,
      [sectionKey]: content
    };

    const updateRequest = axios.put(`${API_BASE_URL}/analyses/${analysisId}`, {
      data: {
        iaReport: updatedIaReport
      }
    }, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    
    // Track the request if we have a global tracker
    const trackedUpdateRequest = globalTrackRequest 
      ? globalTrackRequest(updateRequest, 'updateIaReport')
      : updateRequest;
    
    const response = await trackedUpdateRequest;
    console.log('✅ Analysis iaReport updated:', sectionKey, response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error updating analysis iaReport:', error);
    throw error;
  }
};
